import { useState } from 'react';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import type { TicketDetail } from '../../../api/ticket-types';
import { Button } from '../../../ui/Button';
import { Alert, ErrorAlert } from '../../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../../ui/forms';
import { Modal } from '../../../ui/Modal';

export function RelationshipForm({
  t,
  kind,
  onClose,
}: {
  t: TicketDetail;
  kind: 'parent' | 'dependency' | 'related' | 'blocker' | 'decision' | 'clarify';
  onClose: () => void;
}) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  const [value, setValue] = useState(kind === 'parent' ? (t.parent?.key ?? '') : '');
  const [text, setText] = useState('');
  const rejected = t.descendants.filter(
    (d) => d.status === 'closed' && d.resolution === 'rejected' && !d.scopeDecisionRecorded,
  );
  const titles = {
    parent: 'Set parent',
    dependency: 'Add blocking dependency',
    related: 'Relate a ticket',
    blocker: 'Record blocker',
    decision: 'Parent-scope decision',
    clarify: t.needsClarification ? 'Resolve clarification' : 'Mark as needing clarification',
  };
  const m = useAction(
    () => {
      switch (kind) {
        case 'parent':
          return api.put(`${base}/parent`, { parentKey: value.trim() || null, expectedVersion: t.version });
        case 'dependency':
          return api.post(`${base}/dependencies`, { targetKey: value, expectedVersion: t.version });
        case 'related':
          return api.post(`${base}/related`, { targetKey: value });
        case 'blocker':
          return api.post(`${base}/blockers`, { reason: text });
        case 'decision':
          return api.post(`${base}/scope-decisions`, {
            childTicketKey: value || rejected[0]?.key,
            decision: text,
          });
        case 'clarify':
          return api.put(`${base}/clarification`, { needed: !t.needsClarification, reason: text });
      }
    },
    {
      success: 'Saved',
      invalidate: [
        ['ticket', t.project.key, t.number],
        ['board', t.project.key],
      ],
      onSuccess: onClose,
    },
  );
  const structural = kind === 'parent' || kind === 'dependency';
  return (
    <Modal
      title={titles[kind]}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Save
          </Button>
        </>
      }
    >
      {structural && (
        <Alert tone="info">
          Structure is part of scope. Pending tickets get a new scope revision; accepted tickets must be
          reconsidered first. Cycles are refused.
        </Alert>
      )}
      {(kind === 'parent' || kind === 'dependency' || kind === 'related') && (
        <Field
          label={
            kind === 'parent'
              ? 'Parent ticket'
              : kind === 'dependency'
                ? 'Prerequisite ticket (must be done first)'
                : 'Related ticket'
          }
          hint={
            kind === 'parent'
              ? 'Leave empty to remove the parent.'
              : `Same project, e.g. ${t.project.key}-12.`
          }
        >
          {(p) => (
            <Input
              {...p}
              className="mono"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              placeholder={`${t.project.key}-12`}
              data-autofocus
            />
          )}
        </Field>
      )}
      {kind === 'decision' && (
        <Field label="Rejected child">
          {(p) => (
            <Select {...p} value={value || rejected[0]?.key} onChange={(e) => setValue(e.target.value)}>
              {rejected.map((r) => (
                <option key={r.ticketId} value={r.key}>
                  {r.key}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {(kind === 'blocker' || kind === 'decision' || kind === 'clarify') && (
        <Field
          label={
            kind === 'decision'
              ? 'Why the parent scope remains valid (or what changes)'
              : kind === 'clarify'
                ? t.needsClarification
                  ? 'Resolution note'
                  : 'What needs clarification'
                : 'Reason'
          }
        >
          {(p) => (
            <Textarea {...p} rows={3} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
          )}
        </Field>
      )}
      {kind === 'decision' && (
        <p className="small muted">
          If the parent's intent changes, also reconsider and re-accept the parent.
        </p>
      )}
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
